<template>
  <div class="mx-auto max-w-[var(--container)] px-6 py-8">
    <Commerce-Breadcrumb :items="[{ label: 'خانه', to: 'https://payjib.com' }, { label: 'همهٔ خدمات' }]" />
    <h1 class="mt-4 text-2xl font-extrabold md:text-3xl">
      همهٔ خدمات
    </h1>
    <p class="mt-2 text-muted-foreground">
      خرید و شارژ اشتراک سرویس‌های بین‌المللی.
    </p>
    <div class="mt-6">
      <Commerce-SearchInput v-model="query" placeholder="جستجوی سرویس…" @update:modelValue="handleSearch" />
    </div>

    <div class="relative mt-6">
      <Transition name="fade">
        <button v-show="canPrev" type="button" aria-label="قبلی"
           class="absolute right-0 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background shadow-sm transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground md:flex"
          @click="scrollCats(1)">
          <ChevronRight class="h-5 w-5" />
        </button>
      </Transition>

      <div ref="catScroller" :style="maskStyle"
        class="-mx-6 flex snap-x scroll-px-6 gap-2 overflow-x-auto scroll-smooth px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:scroll-px-0 md:px-0"
        @scroll.passive="updateArrows">
        <button v-for="cat in categories" :key="cat.id"
          class="flex shrink-0 snap-start items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors"
          :class="activeCat === cat.id
            ? 'bg-primary text-primary-foreground border-primary'
            : 'border-border hover:bg-secondary'
            " @click="selectCategory(cat)">
          <span>{{ cat.glyph }}</span>
          {{ cat.name }}
          <span class="text-xs nums opacity-70">
            {{ fa(cat.servicesCount ?? 0) }}
          </span>
        </button>
      </div>

      <Transition name="fade">
        <button v-show="canNext" type="button" aria-label="بعدی"
          class="absolute left-0 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background shadow-sm transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground md:flex"
          @click="scrollCats(-1)">
          <ChevronLeft class="h-5 w-5" />
        </button>
      </Transition>
    </div>

    <div v-if="loadingServices" class="mt-6 grid gap-4 sm:grid-cols-2 ">
      <CardSkeleton v-for="i in 8" :key="i" variant="service" />
    </div>
    <div v-else-if="services.length" class="mt-6">
      <div class="grid gap-4 sm:grid-cols-2">
        <ServiceCard v-for="s in services" :key="s.id" :service="s" />
      </div>

      <Pagination v-if="totalItems > 0" :current-page="currentPage" :items-per-page="itemsPerPage"
        :total-items="totalItems" @update:currentPage="goToPage" appearance="commerce" />
    </div>
    <div v-else class="mt-6">
      <Commerce-EmptyState glyph="⊘" title="سرویسی پیدا نشد" body="هیچ سرویسی با این مشخصات وجود ندارد.">
        <template #action>
          <Commerce-Button variant="outline" @click="clearFilters">
            حذف فیلترها
          </Commerce-Button>
        </template>
      </Commerce-EmptyState>
    </div>
  </div>
</template>

<script setup lang="ts">
const siteUrl = useSiteUrl()

import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useSeoMeta } from '#app'
import { fa } from '~/utils/format'
import { getServiceTypeGlyph } from '~/utils/service-glyphs.ts'

useSeoMeta({
  title: 'همهٔ خدمات - پی‌جیب',
  description: 'خرید و شارژ اشتراک سرویس‌های بین‌المللی.',

  robots: 'index,follow',

  ogTitle: 'همهٔ خدمات - پی‌جیب',
  ogDescription: 'خرید و شارژ اشتراک سرویس‌های بین‌المللی.',
  ogImage: siteUrl('/services/payjib.webp'),
  ogUrl: siteUrl('/services/all'),
  ogType: 'website',
  ogSiteName: 'پی‌جیب',
  ogLocale: 'fa_IR',

  twitterCard: 'summary_large_image',
  twitterTitle: 'همهٔ خدمات - پی‌جیب',
  twitterDescription: 'خرید و شارژ اشتراک سرویس‌های بین‌المللی.',
  twitterImage: siteUrl('/services/payjib.webp'),
})

const router = useRouter()
const route = useRoute()

const query = ref((route.query.q as string) || '')
const activeCat = ref<string | undefined>((route.query.category_id as string) || undefined)

const {
  services,
  loadingServices,
  pagination,
  setFilters,
  setPage,
  clearFilters: clearServiceFilters,
} = useServices({
  per_page: 16,
  filters: {
    name: query.value || undefined,
    category_id: activeCat.value,
  },
})

const totalItems = computed(() => pagination.value?.total ?? 0)

const itemsPerPage = computed(() => pagination.value?.perPage ?? 15)

const currentPage = computed(() => pagination.value?.currentPage ?? 1)

const { categories: apiCategories } = useCategories()

const categories = computed(() =>
  (apiCategories.value ?? []).map((category) => ({
    ...category,
    glyph: getServiceTypeGlyph(category.slug),
  }))
)

let timer: ReturnType<typeof setTimeout>

const updateUrl = () => {
  router.push({
    query: {
      ...(query.value ? { q: query.value } : {}),
      ...(activeCat.value ? { category_id: activeCat.value } : {}),
    },
  })
}

const handleSearch = (value: string) => {
  query.value = value

  clearTimeout(timer)

  timer = setTimeout(() => {
    setFilters({
      name: query.value || undefined,
      category_id: activeCat.value,
    })

    updateUrl()
  }, 800)
}

const selectCategory = (category: (typeof categories.value)[number]) => {
  const isActive = activeCat.value === category.id

  activeCat.value = isActive ? undefined : category.id

  setFilters({
    name: query.value || undefined,
    category_id: activeCat.value,
  })

  updateUrl()
}

const clearFilters = () => {
  query.value = ''
  activeCat.value = undefined

  clearServiceFilters()

  router.push({
    query: {},
  })
}

const goToPage = (page: number) => {
  setPage(page)
}

const catScroller = ref<HTMLElement | null>(null)
const canPrev = ref(false)
const canNext = ref(false)

// لبه‌ها فقط وقتی محو می‌شن که اون سمت هنوز محتوا هست
const maskStyle = computed(() => {
  const left = canNext.value ? 'transparent 0, #000 56px' : '#000 0'
  const right = canPrev.value ? '#000 calc(100% - 56px), transparent 100%' : '#000 100%'
  const gradient = `linear-gradient(to right, ${left}, ${right})`

  return { maskImage: gradient, WebkitMaskImage: gradient }
})

const updateArrows = () => {
  const el = catScroller.value
  if (!el) return
  const scrolled = Math.abs(el.scrollLeft)

  canPrev.value = scrolled > 4
  canNext.value = scrolled + el.clientWidth < el.scrollWidth - 4
}
const scrollCats = (dir: 1 | -1) => {
  const el = catScroller.value
  if (!el) return

  el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' })
}

watch(categories, () => nextTick(updateArrows))

onMounted(() => {
  updateArrows()
  window.addEventListener('resize', updateArrows)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateArrows)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
